import { loadStripe } from "@stripe/stripe-js"

type ConfirmResult = { handled: false } | { handled: true; error?: string; returnUrl?: string }

/**
 * Handles the `#wc-stripe-confirm-pi/si:<orderId>:<clientSecret>:<nonce>` redirect hash
 * returned by the Stripe gateway when a payment intent requires client-side
 * confirmation (e.g. a 3D Secure challenge).
 *
 * Mirrors the flow in the plugin's own checkout JS (`confirmIntent` in upe-classic.js):
 * confirm the intent with Stripe.js, then notify the gateway's AJAX endpoint —
 * regardless of the confirmation outcome — so it can update the order status.
 */
export async function confirmStripeIntentRedirect(opts: { redirect: string; publishableKey: string; paymentMethodId?: string }): Promise<ConfirmResult> {
  const match = opts.redirect.match(/#wc-stripe-confirm-(pi|si):(.+):(.+):(.+)$/)
  if (!match) return { handled: false }
  const [, type, orderId, clientSecret, nonce] = match

  const stripe = await loadStripe(opts.publishableKey)
  if (!stripe) return { handled: true, error: "We couldn't connect to our payment provider. Please try again." }

  const result: any = type === "si" ? await stripe.confirmCardSetup(clientSecret) : await stripe.confirmCardPayment(clientSecret)

  const intentId = result.paymentIntent?.id ?? result.setupIntent?.id ?? result.error?.payment_intent?.id ?? result.error?.setup_intent?.id

  const body = new URLSearchParams({
    order_id: orderId,
    intent_id: intentId ?? "",
    _ajax_nonce: nonce,
  })
  if (opts.paymentMethodId) body.set("payment_method_id", opts.paymentMethodId)

  const response = await fetch("/?wc-ajax=wc_stripe_update_order_status", {
    method: "POST",
    body,
    credentials: "same-origin",
  })
  const payload: any = await response.json().catch(() => null)

  if (result.error) {
    return { handled: true, error: result.error.message ?? "Your card was declined, or there was an error processing your payment. Please try again." }
  }
  if (!payload?.success) {
    const message = typeof payload?.data?.error?.message === "string" ? payload.data.error.message : "There was an error processing your payment. Please try again."
    return { handled: true, error: message }
  }
  return { handled: true, returnUrl: payload.data?.return_url }
}
